import { Component, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export type M3BadgeSize = 'small' | 'large';
export type M3BadgePosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

@Component({
  selector: 'app-m3-badge',
  standalone: true,
  imports: [CommonModule],
  host: {
    '[attr.data-position]': 'position()',
    '[attr.data-size]': 'size()',
  },
  template: `
    <span
      [class]="badgeClasses()"
      [attr.aria-label]="ariaLabel()"
      role="status"
    >
      @if (size() === 'large' && content(); as displayContent) {
        {{ displayContent }}
      }
    </span>
  `,
  styles: [
    `
      :host {
        position: absolute;
        display: block;
        pointer-events: none;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }

      .m3-badge {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(
          --mat-sys-typescale-label-small-font-family-name,
          'Inter Variable',
          'Inter',
          'Segoe UI',
          'system-ui',
          '-apple-system',
          'BlinkMacSystemFont',
          'Helvetica Neue',
          'Arial',
          sans-serif
        );
        font-weight: 500;
        box-sizing: border-box;
        z-index: 1;
        background-color: var(--md-sys-color-error);
      }

      .m3-badge-small {
        width: 6px;
        height: 6px;
        border-radius: var(--md-sys-shape-corner-full);
      }

      .m3-badge-large {
        min-width: 16px;
        height: 16px;
        border-radius: 8px; /* Fixed 8px radius to maintain circular shape */
        color: var(--md-sys-color-on-error);
        font-size: 11px;
        line-height: 16px;
        letter-spacing: 0.5px;
        padding: 0 4px;
      }

      /* Top Right positioning */
      :host([data-position='top-right']) .m3-badge-small {
        top: -3px;
        right: -3px;
      }

      :host([data-position='top-right']) .m3-badge-large {
        top: -8px;
        right: -8px;
      }

      /* Top Left positioning */
      :host([data-position='top-left']) .m3-badge-small {
        top: -3px;
        left: -3px;
      }

      :host([data-position='top-left']) .m3-badge-large {
        top: -8px;
        left: -8px;
      }

      /* Bottom Right positioning */
      :host([data-position='bottom-right']) .m3-badge-small {
        bottom: -3px;
        right: -3px;
      }

      :host([data-position='bottom-right']) .m3-badge-large {
        bottom: -8px;
        right: -8px;
      }

      /* Bottom Left positioning */
      :host([data-position='bottom-left']) .m3-badge-small {
        bottom: -3px;
        left: -3px;
      }

      :host([data-position='bottom-left']) .m3-badge-large {
        bottom: -8px;
        left: -8px;
      }
    `,
  ],
})
export class M3BadgeComponent {
  // Inputs
  content = input<string | number>();
  size = input<M3BadgeSize>('small');
  position = input<M3BadgePosition>('top-right');
  ariaLabel = input<string>();

  // Computed properties
  badgeClasses = computed(() => {
    const baseClasses = ['m3-badge'];

    if (this.size() === 'small') {
      baseClasses.push('m3-badge-small');
    } else {
      baseClasses.push('m3-badge-large');
    }

    return baseClasses.join(' ');
  });

  displayContent = computed(() => {
    const rawContent = this.content();
    if (rawContent === null || rawContent === undefined) {
      return '';
    }

    const contentStr = String(rawContent);

    // Limit to 4 characters including a +
    if (contentStr.length > 4) {
      return contentStr.substring(0, 3) + '+';
    }

    // For numbers, show 999+ pattern
    if (typeof rawContent === 'number' && rawContent > 999) {
      return '999+';
    }

    return contentStr;
  });
}
